import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, api, type Product } from '../api';
import { ErrorBox, Field } from '../components/ui';
import { useSettings } from '../hooks';

export function ProductNewPage() {
  const nav = useNavigate();
  const settings = useSettings();
  const [f, setF] = useState({ itemNumber: '', listingTitle: '', referencePrice: '', referenceCurrency: '', description: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const cur = f.referenceCurrency || settings.data?.defaultCurrency || 'CAD';
  const inp = (k: keyof typeof f) => ({ value: f[k], onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value }) });

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ product: Product }>('/products', {
        body: {
          itemNumber: f.itemNumber,
          listingTitle: f.listingTitle || null,
          referencePrice: f.referencePrice || null,
          referenceCurrency: f.referencePrice ? cur : null,
          description: f.description || null,
        },
      });
      nav(`/products/${r.product.id}`);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const existingId = error instanceof ApiError && error.code === 'item_number_exists' ? error.details?.productId : null;

  return (
    <form className="narrow card" onSubmit={save}>
      <h1>New product</h1>
      <p className="muted small">For items not in a manifest CSV. Enter only information you have from the item or paperwork.</p>
      <Field label="Item number (required)" hint="Stored exactly as typed; leading zeros are kept.">
        <input {...inp('itemNumber')} required className="mono" autoFocus />
      </Field>
      <Field label="Title">
        <input {...inp('listingTitle')} maxLength={500} />
      </Field>
      <div className="grid2">
        <Field label="Reference price" hint="Blank = unknown">
          <input {...inp('referencePrice')} inputMode="decimal" />
        </Field>
        <Field label="Currency">
          <select value={cur} onChange={(e) => setF({ ...f, referenceCurrency: e.target.value })}>
            {[...new Set([cur, 'CAD', 'USD'])].map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Description (optional)">
        <textarea {...inp('description')} rows={3} />
      </Field>
      <ErrorBox error={error} />
      {existingId && <Link to={`/products/${existingId}`}>Open the existing product</Link>}
      <div className="row gap mt">
        <button className="btn btn-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Create product'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => nav(-1)}>
          Back
        </button>
      </div>
    </form>
  );
}
